const API_URL = process.env.REACT_APP_API_URL || "";

function isUsableStoredToken(value) {
  return Boolean(value && value !== "undefined" && value !== "null");
}

export function getStoredAdminToken(key) {
  const value = localStorage.getItem(key);
  return isUsableStoredToken(value) ? value : null;
}

export function getStoredAdminRole() {
  const value = localStorage.getItem("adminRole");
  return isUsableStoredToken(value) ? value : null;
}

export function setStoredAdminRole(role) {
  if (isUsableStoredToken(role)) {
    localStorage.setItem("adminRole", String(role));
    return;
  }

  localStorage.removeItem("adminRole");
}

export function clearStoredAdminSession() {
  localStorage.removeItem("idToken");
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("adminRole");
}

export function isManagementAdminRole(role) {
  return Boolean(role) && String(role).trim().toLowerCase() !== "host";
}

export async function refreshAdminTokens() {
  const refreshToken = getStoredAdminToken("refreshToken");
  if (!refreshToken) return false;

  try {
    const response = await fetch(`${API_URL}/admin/refresh-token`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) return false;

    const data = await response.json();
    if (!data?.idToken || !data?.accessToken) return false;

    localStorage.setItem("idToken", data.idToken);
    localStorage.setItem("accessToken", data.accessToken);
    if (data.refreshToken) {
      localStorage.setItem("refreshToken", data.refreshToken);
    }

    return true;
  } catch (_) {
    return false;
  }
}

async function buildResponseError(response) {
  let data = null;

  try {
    data = await response.json();
  } catch (_) {
    data = null;
  }

  const error = new Error(
    data?.error || data?.message || `Request failed with status ${response.status}`
  );
  error.response = {
    status: response.status,
    data,
  };
  return error;
}

export async function runAdminAuthedRequest(requestFn, { onAuthFailure } = {}) {
  let idToken = getStoredAdminToken("idToken");

  if (!idToken) {
    if (typeof onAuthFailure === "function") {
      onAuthFailure();
    }
    return null;
  }

  try {
    return await requestFn(idToken);
  } catch (error) {
    if (error?.response?.status !== 401) {
      throw error;
    }

    const refreshed = await refreshAdminTokens();
    if (!refreshed) {
      if (typeof onAuthFailure === "function") {
        onAuthFailure();
      }
      return null;
    }

    idToken = getStoredAdminToken("idToken");
    if (!idToken) {
      if (typeof onAuthFailure === "function") {
        onAuthFailure();
      }
      return null;
    }

    return requestFn(idToken);
  }
}

export async function fetchAdminProfile({ onAuthFailure } = {}) {
  const profile = await runAdminAuthedRequest(
    async (idToken) => {
      const response = await fetch(`${API_URL}/admin/profile`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw await buildResponseError(response);
      }

      return response.json();
    },
    { onAuthFailure }
  );

  if (profile?.role) {
    setStoredAdminRole(profile.role);
  }

  return profile;
}
